import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import api from '../utils/api';
import FacilityAccessDisplay from './FacilityAccessDisplay';

const PlanSelector = ({ selectedPlanId, onPlanSelect, error, memberTypeId, showMemberTypeSpecific = true, currentUserPlan = null }) => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  // Check if user's current plan allows instant activation
  const currentPlanAllowsInstantActivation = currentUserPlan?.allowInstantNextPlanActivation === true;

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);

      // Build query parameters
      const queryParams = new URLSearchParams();
      if (memberTypeId && showMemberTypeSpecific && typeof memberTypeId === 'string') {
        queryParams.append('memberType', memberTypeId);
      }

      const queryString = queryParams.toString();
      const url = `/plans${queryString ? `?${queryString}` : ''}`;

      const response = await api.get(url);
      setPlans(response.data.data);
      setFetchError('');
    } catch (err) {
      setFetchError(t('planSelector:errors.fetchFailed'));
      console.error('Error fetching plans:', err);
    } finally {
      setLoading(false);
    }
  }, [memberTypeId, showMemberTypeSpecific, t]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const formatPrice = (plan) => {
    // If member type filtering is enabled and plan has calculated price, use that
    if (showMemberTypeSpecific && memberTypeId && plan.calculatedPrice !== undefined) {
      return `₺${plan.calculatedPrice.toFixed(2)}`;
    }
    // Otherwise use the base price
    return `₺${plan.price.toFixed(2)}`;
  };

  const getDiscountInfo = (plan) => {
    if (showMemberTypeSpecific && memberTypeId && plan.calculatedPrice !== undefined && plan.price > plan.calculatedPrice) {
      const discountAmount = plan.price - plan.calculatedPrice;
      const discountPercentage = ((discountAmount / plan.price) * 100).toFixed(0);
      return {
        hasDiscount: true,
        originalPrice: plan.price,
        discountAmount,
        discountPercentage
      };
    }
    return { hasDiscount: false };
  };

  const formatDuration = (duration) => {
    if (duration === 1) return t('planSelector:duration.oneDay');
    if (duration < 30) return t('planSelector:duration.days', { count: duration });
    if (duration === 30) return t('planSelector:duration.oneMonth');
    if (duration < 365) return t('planSelector:duration.months', { count: Math.round(duration / 30) });
    return t('planSelector:duration.years', { count: Math.round(duration / 365) });
  };

  const handlePlanChange = (event) => {
    const planId = event.target.value;
    const selectedPlan = plans.find(plan => plan._id === planId);
    onPlanSelect(selectedPlan);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {fetchError}
      </Alert>
    );
  }

  if (plans.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('planSelector:empty.noPlans')}
      </Alert>
    );
  }

  return (
    <Box>
      <FormControl component="fieldset" fullWidth>
        <FormLabel component="legend" sx={{ mb: 2 }}>
          <Typography variant="h6">
            {t('planSelector:title')}
          </Typography>
        </FormLabel>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <RadioGroup
          value={selectedPlanId || ''}
          onChange={handlePlanChange}
        >
          <Grid container spacing={2}>
            {plans.map((plan, index) => (
              <Grid size={{xs:12 , md:6}} key={plan._id}>
                <Card
                  sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: selectedPlanId === plan._id ? '2px solid' : '1px solid',
                    borderColor: selectedPlanId === plan._id ? 'primary.main' : 'divider',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: 2
                    }
                  }}
                  onClick={() => {
                    const selectedPlan = plans.find(p => p._id === plan._id);
                    onPlanSelect(selectedPlan);
                  }}
                >
                  <FormControlLabel
                    value={plan._id}
                    control={<Radio />}
                    label=""
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      m: 0
                    }}
                  />
                  
                  {/* Highlight popular plan */}
                  {plans.length >= 3 && index === Math.floor(plans.length / 2) && (
                    <Chip
                      label={t('planSelector:popular')}
                      color="primary"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8
                      }}
                    />
                  )}

                  <CardContent sx={{ pr: 6 }}>
                    <Typography variant="h6" component="h3" gutterBottom>
                      {plan.name}
                    </Typography>

                    {/* Show instant activation badge if user's current plan allows it */}
                    {currentPlanAllowsInstantActivation && (
                      <Box sx={{ mb: 1.5 }}>
                        <Chip
                          label={t('planSelector:instantActivation.planCardBadge')}
                          color="secondary"
                          size="small"
                          sx={{ mb: 0.5 }}
                        />
                        <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                          <Typography variant="caption">
                            {t('planSelector:instantActivation.planCardWarning')}
                          </Typography>
                        </Alert>
                      </Box>
                    )}

                    <Box sx={{ mb: 2 }}>
                      {(() => {
                        const discountInfo = getDiscountInfo(plan);
                        return (
                          <>
                            {discountInfo.hasDiscount && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  sx={{ textDecoration: 'line-through' }}
                                >
                                  ₺{discountInfo.originalPrice.toFixed(2)}
                                </Typography>
                                <Chip 
                                  label={t('planSelector:discount.label', { percentage: discountInfo.discountPercentage })}
                                  color="success"
                                  size="small"
                                />
                              </Box>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                              <Typography variant="h4" component="div" color="primary">
                                {formatPrice(plan)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                / {formatDuration(plan.duration)}
                              </Typography>
                            </Box>
                          </>
                        );
                      })()}
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {plan.description}
                    </Typography>

                    <Typography variant="subtitle2" gutterBottom>
                      {t('planSelector:includes')}
                    </Typography>
                    <List dense sx={{ py: 0 }}>
                      {(() => {
                        // Use allFeatures if available (member type specific), otherwise use base features
                        const featuresToShow = plan.allFeatures || plan.features;
                        const displayFeatures = featuresToShow.slice(0, 3);
                        
                        return (
                          <>
                            {displayFeatures.map((feature, featureIndex) => (
                              <ListItem key={featureIndex} sx={{ px: 0, py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 24 }}>
                                  <CheckIcon color="success" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={feature} 
                                  slotProps={{ primary: { variant: 'body2' } }}
                                />
                              </ListItem>
                            ))}
                            {featuresToShow.length > 3 && (
                              <ListItem sx={{ px: 0, py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 24 }}>
                                  <CheckIcon color="success" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={t('planSelector:moreFeatures', { count: featuresToShow.length - 3 })}
                                  slotProps={{ primary: { variant: 'body2', fontStyle: 'italic' } }}
                                />
                              </ListItem>
                            )}
                          </>
                        );
                      })()}
                    </List>

                    {/* Facility Access Display */}
                    {plan.facilityAccess && plan.facilityAccess.length > 0 && (
                      <FacilityAccessDisplay 
                        facilityAccess={plan.facilityAccess}
                        compact={true}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </RadioGroup>
      </FormControl>
    </Box>
  );
};

export default PlanSelector;